import React from 'react';
import { NavLink } from 'react-router-dom';
import { LogOut } from 'lucide-react';
import { cn } from '@/lib/utils';
import Avatar from '../Avatar';
import Divider from '../Divider';
import Icon from '../Icon';
import Logo from '../Logo';
import WhatsAppCard from './WhatsAppCard';
import { NavItem, ShellUser } from './types';

interface SideNavProps {
  items: NavItem[];
  user: ShellUser;
  onLogout: () => void;
}

const SideNavLink: React.FC<{ item: NavItem }> = ({ item }) => (
  <NavLink
    to={item.to}
    className={({ isActive }) =>
      cn(
        'tw-flex tw-w-full tw-items-center tw-gap-3 tw-rounded-[10px] tw-px-3 tw-py-[11px] tw-text-[15px] tw-leading-5 tw-transition-colors',
        'focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-ds-brand',
        isActive
          ? 'tw-bg-ds-brand-tint tw-font-semibold tw-text-ds-brand-text'
          : 'tw-font-medium tw-text-ds-text-secondary hover:tw-bg-ds-subtle hover:tw-text-ds-text-primary',
      )
    }
  >
    <Icon icon={item.icon} size={20} />
    {item.label}
  </NavLink>
);

/** Desktop sidebar (264px): logo, navigation, WhatsApp card and the signed-in user. */
const SideNav: React.FC<SideNavProps> = ({ items, user, onLogout }) => {
  const main = items.filter((i) => i.group !== 'account');
  const account = items.filter((i) => i.group === 'account');
  return (
    <aside className="tw-sticky tw-top-0 tw-hidden tw-h-dvh tw-w-[264px] tw-shrink-0 tw-flex-col tw-gap-1 tw-overflow-y-auto tw-border-r tw-border-ds-border-default tw-bg-ds-surface tw-px-4 tw-pb-5 tw-pt-7 lg:tw-flex">
      <div className="tw-px-3 tw-pb-7">
        <Logo size="sm" />
      </div>
      <nav aria-label="Main" className="tw-flex tw-flex-col tw-gap-1">
        {main.map((item) => (
          <SideNavLink key={item.to} item={item} />
        ))}
        {account.length > 0 && (
          <>
            <Divider className="tw-my-2" />
            {account.map((item) => (
              <SideNavLink key={item.to} item={item} />
            ))}
          </>
        )}
      </nav>
      <div className="tw-flex-1" />
      <WhatsAppCard />
      <Divider className="tw-mb-3 tw-mt-4" />
      <div className="tw-flex tw-items-center tw-gap-3 tw-p-1">
        <Avatar name={user.name} />
        <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-0.5">
          <p className="tw-truncate tw-text-[14px] tw-font-semibold tw-leading-[18px] tw-text-ds-text-primary">{user.name || 'Customer'}</p>
          {user.contact && <p className="tw-truncate tw-text-[12px] tw-leading-4 tw-text-ds-text-secondary">{user.contact}</p>}
        </div>
        <button
          type="button"
          onClick={onLogout}
          aria-label="Log out"
          title="Log out"
          className="tw-flex tw-h-8 tw-w-8 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-lg tw-text-ds-text-secondary tw-transition-colors hover:tw-bg-ds-subtle hover:tw-text-ds-text-primary focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-ds-brand"
        >
          <Icon icon={LogOut} size={20} />
        </button>
      </div>
    </aside>
  );
};

export default SideNav;
